import { FC } from "react";

/**
 * Главная иллюстрация 404: «404», где вместо нуля — пустой крафт-лоток.
 * Обед уехал не по адресу: еда разлетелась вокруг, внутри остались только
 * крошки, а курьер всё ещё нарезает круги по пунктирной орбите.
 *
 * Всё рисуется путями — резкость на любой плотности экрана, вес ~6 КБ
 * вместо 900 КБ у прежнего PNG. Анимируются только transform и opacity;
 * при prefers-reduced-motion сцена замирает.
 */

const UID = "nf";

/**
 * Округление координат рифления до сотых. Без него сервер и браузер
 * сериализуют один и тот же float по-разному в последнем знаке
 * (-44.430577700900734 против -44.43057770090073) — React считает это
 * расхождением разметки и ругается на гидратацию.
 */
const round = (n: number): number => Number(n.toFixed(2));

/**
 * Цифра «4»: диагональ с перекладиной и стойка. Бокс 108×148.
 *
 * Оба штриха — подпути одного path. Отдельным путём стойка была бы строго
 * вертикальной, то есть с нулевой шириной bbox, а градиент в единицах
 * objectBoundingBox такие элементы не отрисовывает вовсе.
 */
const Digit: FC = () => (
  <path
    d="M78 10 L14 112 H122 M78 10 V158"
    stroke={`url(#${UID}-digit)`}
    strokeWidth="24"
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="none"
  />
);

export const LostIllustration: FC<{ className?: string }> = ({ className }) => (
  <div className={className} aria-hidden>
    <style>{`
      .nf-g{transform-box:fill-box;transform-origin:center}
      @keyframes nf-spin{to{transform:rotate(360deg)}}
      @keyframes nf-spin-ccw{to{transform:rotate(-360deg)}}
      @keyframes nf-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-9px)}}
      @keyframes nf-float-sm{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
      @keyframes nf-twinkle{0%,100%{opacity:.15;transform:scale(.8)}50%{opacity:.65;transform:scale(1)}}
      .nf-orbit,.nf-ring{transform-box:view-box}
      .nf-orbit{transform-origin:310px 140px;animation:nf-spin 22s linear infinite}
      .nf-ring{transform-origin:310px 140px;animation:nf-spin-ccw 80s linear infinite}
      .nf-b{animation:nf-float-sm 6s ease-in-out infinite}
      .nf-f1{animation:nf-float 7s ease-in-out infinite}
      .nf-f2{animation:nf-float 6s ease-in-out 1.4s infinite}
      .nf-f3{animation:nf-float-sm 5.5s ease-in-out .7s infinite}
      .nf-f4{animation:nf-float 8s ease-in-out 2.1s infinite}
      .nf-t1{animation:nf-twinkle 3.4s ease-in-out infinite}
      .nf-t2{animation:nf-twinkle 4.2s ease-in-out 1.3s infinite}
      .nf-t3{animation:nf-twinkle 3.8s ease-in-out 2.2s infinite}
      @media (prefers-reduced-motion: reduce){
        .nf-orbit,.nf-ring,.nf-b,.nf-f1,.nf-f2,.nf-f3,.nf-f4,.nf-t1,.nf-t2,.nf-t3{animation:none}
      }
    `}</style>

    <svg viewBox="0 0 620 300" fill="none" className="h-full w-full">
      <defs>
        {/* Цифры: кремовый с лёгким объёмом сверху вниз. */}
        <linearGradient id={`${UID}-digit`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f7f5f0" />
          <stop offset="100%" stopColor="#d2d9d7" />
        </linearGradient>
        {/* Крафт-борт лотка. */}
        <linearGradient id={`${UID}-kraft`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e5b880" />
          <stop offset="100%" stopColor="#b9834b" />
        </linearGradient>
        {/* Пустое нутро: тёмное — заодно читается как «дырка» нуля. */}
        <radialGradient id={`${UID}-inside`} cx="50%" cy="34%" r="72%">
          <stop offset="0%" stopColor="#3a3350" />
          <stop offset="100%" stopColor="#241f31" />
        </radialGradient>
        <radialGradient id={`${UID}-shadow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(0,0,0,0.45)" />
          <stop offset="60%" stopColor="rgba(0,0,0,0.18)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
        <radialGradient id={`${UID}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(208,237,28,0.16)" />
          <stop offset="60%" stopColor="rgba(208,237,28,0.05)" />
          <stop offset="100%" stopColor="rgba(208,237,28,0)" />
        </radialGradient>
        {/* Помидор держим красным: рядом уже есть оранжевая долька,
            два одинаково тёплых шара сливались бы в одно пятно. */}
        <radialGradient id={`${UID}-tom`} cx="36%" cy="30%" r="72%">
          <stop offset="0%" stopColor="#f79b78" />
          <stop offset="55%" stopColor="#e2603c" />
          <stop offset="100%" stopColor="#c2412a" />
        </radialGradient>
        <radialGradient id={`${UID}-blue`} cx="36%" cy="30%" r="72%">
          <stop offset="0%" stopColor="#5c5586" />
          <stop offset="65%" stopColor="#443d63" />
          <stop offset="100%" stopColor="#37324e" />
        </radialGradient>
      </defs>

      {/* Свечение за композицией */}
      <circle cx="310" cy="140" r="180" fill={`url(#${UID}-glow)`} />

      {/* Цифры */}
      <g transform="translate(30 66)">
        <Digit />
      </g>
      <g transform="translate(468 66)">
        <Digit />
      </g>

      {/* ===== Ноль — пустой крафт-лоток сверху ===== */}
      <g className="nf-b">
        {/* Пунктирная орбита: маршрут, по которому уехал обед */}
        <g className="nf-ring">
          <circle
            cx="310"
            cy="140"
            r="112"
            stroke="rgba(242,239,232,0.22)"
            strokeWidth="1.6"
            strokeDasharray="3 14"
            strokeLinecap="round"
          />
        </g>

        <g transform="translate(310 140)">
          <ellipse
            cx="0"
            cy="98"
            rx="84"
            ry="13"
            fill={`url(#${UID}-shadow)`}
          />
          {/* борт */}
          <circle
            r="68"
            fill="none"
            stroke={`url(#${UID}-kraft)`}
            strokeWidth="28"
          />
          {/* рифление борта — по нему круглый крафт-лоток и узнаётся */}
          <g stroke="rgba(94,58,22,0.14)" strokeWidth="1.6" strokeLinecap="round">
            {Array.from({ length: 36 }, (_, i) => {
              const a = (i * 10 * Math.PI) / 180;
              const cos = Math.cos(a);
              const sin = Math.sin(a);
              return (
                <line
                  key={i}
                  x1={round(cos * 58)}
                  y1={round(sin * 58)}
                  x2={round(cos * 79)}
                  y2={round(sin * 79)}
                />
              );
            })}
          </g>
          {/* внешняя кромка — силуэт на тёмном фоне */}
          <circle
            r="82"
            fill="none"
            stroke="rgba(0,0,0,0.22)"
            strokeWidth="2.5"
          />
          {/* нутро */}
          <circle r="55" fill={`url(#${UID}-inside)`} />
          <circle r="55" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="7" />
          {/* блик на кромке */}
          <path
            d="M-48.1 -48.1 A68 68 0 0 1 48.1 -48.1"
            stroke="rgba(255,255,255,0.34)"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />
          {/* от обеда остались только крошки — осели на дне */}
          <g>
            <ellipse cx="-10" cy="35" rx="30" ry="8" fill="rgba(0,0,0,0.25)" />
            <circle cx="-25" cy="30" r="4.6" fill="#c4dd1f" />
            <circle cx="-10" cy="34" r="3.8" fill="#e0a970" />
            <circle cx="3" cy="28" r="2.8" fill="#a8783f" />
            <circle cx="15" cy="33" r="4.2" fill="#a8c215" />
            <circle cx="-34" cy="21" r="2.6" fill="#e0a970" opacity=".85" />
          </g>
        </g>

        {/* Курьер на орбите */}
        <g className="nf-orbit">
          <g transform="translate(310 28)">
            <circle r="14" fill="#d0ed1c" />
            <circle
              r="20"
              fill="none"
              stroke="rgba(208,237,28,0.22)"
              strokeWidth="1.4"
            />
            <path
              d="M-3.5 -1.5 H2 L0 -4.5 L5.5 0 L0 4.5 L2 1.5 H-3.5 Z"
              fill="#302a41"
            />
          </g>
        </g>
      </g>

      {/* ===== Разлетевшаяся еда ===== */}

      {/* Лист салата */}
      <g className="nf-f1">
        <g transform="translate(86 20) rotate(-18)">
          <path
            d="M2 34C-3 15 10 0 36 0c3 23-8 37-31 37-1.6 0-2.4-1.6-3-3Z"
            fill="#d0ed1c"
          />
          <path
            d="M7 33C13 22 21 12 31 5"
            stroke="#302a41"
            strokeWidth="2"
            strokeLinecap="round"
            opacity=".45"
          />
          <path
            d="M12 26l-6.5-2.4M19 19l-7-3M26 12l-6.5-3"
            stroke="#302a41"
            strokeWidth="1.7"
            strokeLinecap="round"
            opacity=".3"
          />
        </g>
      </g>

      {/* Долька апельсина */}
      <g className="nf-f2">
        <g transform="translate(44 248) rotate(-8)">
          <circle cx="18" cy="18" r="18" fill="#f2a65a" />
          <circle cx="18" cy="18" r="13.5" fill="#f7bd7f" />
          <g stroke="#f2a65a" strokeWidth="2.4" strokeLinecap="round">
            <path d="M18 5v26M7.5 11.5l21 13M7.5 24.5l21-13" />
          </g>
          <path
            d="M30 3c2.6-3.4 7-5 10.6-4.2"
            stroke="#a8c215"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      </g>

      {/* Черника */}
      <g className="nf-f3">
        <g transform="translate(540 24)">
          <circle r="8" fill={`url(#${UID}-blue)`} />
          <circle cx="13" cy="10" r="6" fill={`url(#${UID}-blue)`} />
          <circle cx="-2.5" cy="-2.8" r="2.1" fill="#7a72a8" />
          <circle cx="11" cy="7.8" r="1.6" fill="#7a72a8" />
        </g>
      </g>

      {/* Помидорка */}
      <g className="nf-f4">
        <g transform="translate(556 260)">
          <circle r="16" fill={`url(#${UID}-tom)`} />
          <circle r="9.6" fill="#f2947a" opacity=".45" />
          <ellipse
            cx="-5.4"
            cy="-6"
            rx="3.8"
            ry="2.4"
            fill="rgba(255,255,255,0.6)"
          />
          <path
            d="M0 -16c1-4 4-6.5 7.5-7"
            stroke="#a8c215"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      </g>

      {/* Искры */}
      <g stroke="#d0ed1c" strokeWidth="2.6" strokeLinecap="round">
        <g className="nf-g nf-t1">
          <path d="M214 34v13M207.5 40.5h13" />
        </g>
        <g className="nf-g nf-t2">
          <path d="M416 254v12M410 260h12" />
        </g>
        <g className="nf-g nf-t3">
          <path d="M480 18v11M474.5 23.5h11" />
        </g>
      </g>

      {/* Крошки-точки вокруг */}
      <g fill="rgba(242,239,232,0.2)">
        <circle cx="170" cy="252" r="2.4" />
        <circle cx="248" cy="14" r="2" />
        <circle cx="376" cy="272" r="2.2" />
        <circle cx="600" cy="150" r="1.9" />
        <circle cx="18" cy="120" r="2.1" />
      </g>
    </svg>
  </div>
);
